import { useEffect, useState } from "react";

/**
 * Minimal History-API router — no routing library dependency. Routes are clean
 * paths like /overview, /migration, /migration/workloads/:id (no "#"). The shell
 * reads the current path and renders the matching screen. The dev server and the
 * local runtime both fall back to index.html so deep links / reloads work.
 */

export interface Route {
  readonly path: string; // e.g. "/migration/workloads/:id"
  readonly params: Record<string, string>;
}

/** Current in-app path (query stripped), defaulting to /overview at the root. */
function currentPath(): string {
  const p = window.location.pathname;
  return p && p !== "/" ? p : "/overview";
}

/** Match the current path against a set of patterns; returns the first match. */
export function matchRoute(patterns: readonly string[], raw: string): Route | null {
  const reqSegments = raw.split("?")[0]!.split("/").filter(Boolean);
  for (const pattern of patterns) {
    const pSeg = pattern.split("/").filter(Boolean);
    if (pSeg.length !== reqSegments.length) continue;
    const params: Record<string, string> = {};
    let ok = true;
    for (let i = 0; i < pSeg.length; i++) {
      const p = pSeg[i]!;
      const r = reqSegments[i]!;
      if (p.startsWith(":")) params[p.slice(1)] = decodeURIComponent(r);
      else if (p !== r) {
        ok = false;
        break;
      }
    }
    if (ok) return { path: pattern, params };
  }
  return null;
}

/** Subscribe to path changes (back/forward + in-app navigate). */
export function useRoutePath(): string {
  const [path, setPath] = useState(currentPath);
  useEffect(() => {
    const onChange = () => setPath(currentPath());
    window.addEventListener("popstate", onChange);
    return () => window.removeEventListener("popstate", onChange);
  }, []);
  return path;
}

/** Navigate within the app (pushes history and notifies subscribers). May include a ?query. */
export function navigate(to: string): void {
  if (to === window.location.pathname + window.location.search) return;
  window.history.pushState(null, "", to);
  window.dispatchEvent(new PopStateEvent("popstate"));
}
